function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, { components as ReactSelectComponents } from 'react-select';
import { FormControl } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import { useTheme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CancelIcon from '@material-ui/icons/Cancel';
import PropTypes from 'prop-types';
import { DefaultText } from '../content';
const NoOptionsMessage = props => /*#__PURE__*/React.createElement(DefaultText, _extends({
  color: "text-secondary MuiAutocomplete-noOptions"
}, props.innerProps), props.children);
const inputComponent = ({
  inputRef,
  ...props
}) => /*#__PURE__*/React.createElement("div", _extends({
  ref: inputRef
}, props));
const Control = props => {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: {
      TextFieldProps
    }
  } = props;
  return /*#__PURE__*/React.createElement(TextField, _extends({
    InputProps: {
      inputComponent,
      inputProps: {
        ref: innerRef,
        style: {
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          height: 'auto'
        },
        children,
        ...innerProps
      }
    }
  }, TextFieldProps));
};
const Option = props => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(MenuItem, _extends({
    key: Date.now(),
    style: {
      backgroundColor: props.isDisabled && theme.palette.error.light
    },
    ref: props.innerRef,
    selected: props.isFocused,
    component: "div"
  }, props.innerProps), props.children);
};
const Placeholder = props => {
  const {
    t
  } = useTranslation();
  const {
    innerProps = {}
  } = props;
  return /*#__PURE__*/React.createElement("p", _extends({
    className: "MuiAutocomplete-placeholder MuiTypography-colorTextSecondary"
  }, innerProps), `${t('BOOK_WIDGET.select')}...`);
};
const SingleValue = props => /*#__PURE__*/React.createElement("p", props.innerProps, props.children);
const ValueContainer = props => {
  const renderStyle = props.selectProps.dropdownPortal;
  const params = renderStyle ? {
    style: {
      overflow: 'hidden',
      flexWrap: 'nowrap'
    }
  } : {};
  return /*#__PURE__*/React.createElement("div", _extends({
    className: "MuiAutocomplete-values"
  }, params), props.children);
};
const MultiValue = props => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(Chip, {
    style: {
      backgroundColor: props.data.isDisabled && theme.palette.error.light
    },
    tabIndex: -1,
    label: props.children,
    onDelete: props.removeProps.onClick,
    size: "small",
    deleteIcon: /*#__PURE__*/React.createElement(CancelIcon, _extends({}, props.removeProps, {
      size: "small"
    }))
  });
};
const Menu = props => /*#__PURE__*/React.createElement(Paper, _extends({
  square: true
}, props.innerProps), props.children);

// Chrome guesses some generic text inputs (react-select's underlying search input has
// no explicit autocomplete purpose of its own) might be a payment field, and on a
// non-HTTPS origin shows an "Automatic payment methods filling is disabled..." bubble
// that visually overlaps the option list, blocking clicks on it entirely (found in
// Coaching's athlete onboarding "pick a customer" picker, 2026-07-05, local dev over
// http). autoComplete="off" doesn't guarantee Chrome respects it for this specific
// heuristic, but it's the standard mitigation and safe to apply everywhere this shared
// component is used.
const Input = props => /*#__PURE__*/React.createElement(ReactSelectComponents.Input, _extends({}, props, {
  autoComplete: "off"
}));
const components = {
  Control,
  Input,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer
};
const Autocomplete = ({
  id,
  name,
  label,
  onChange,
  placeholder,
  isMultiSelect,
  options,
  value,
  throwError = false,
  margin = 'normal',
  className,
  fullWidth = false,
  required = true,
  dropdownPortal = false,
  error,
  helperText,
  ...rest
}) => {
  const {
    t
  } = useTranslation();
  const [localValue, setLocalValue] = useState(value?.map(x => ({
    value: x.value || x.id,
    label: x.label || x.name
  })));
  const [errorMessage, setErrorMessage] = useState(null);
  const elementReference = useRef();
  useEffect(() => {
    if (!elementReference) {
      return;
    }
    const validationListener = event => {
      event.stopPropagation();
      setErrorMessage({
        message: event.target.errormsg,
        param: event.target.errormsgparam
      });
      setLocalValue(event.target.value);
    };
    const elements = id ? document.querySelectorAll(`.${id} [name="${elementReference.current.props.name}"]`) : document.getElementsByName(elementReference.current.props.name);
    if (!elements || !elements.length) {
      return;
    }
    if (elements.length > 1 && !isMultiSelect) {
      throw new Error('Autocomplete duplicate components error');
    }
    const element = elements[0];
    required && element.setAttribute('requiredstr', 'true');
    element.addEventListener('invalid-data', validationListener);
    return () => {
      element && element.removeEventListener('invalid-data', validationListener);
    };
  }, [elementReference, id, isMultiSelect, label, required]);
  const triggerChangeForHiddenField = elementName => {
    const element = id ? document.querySelector(`.${id} [name="${elementName}"]`) : document.querySelector(`[name="${elementName}"]`);
    const ev = new Event('input', {
      bubbles: true
    });
    ev.simulated = true;
    element.type = 'text';
    const tracker = element._valueTracker;
    if (tracker) {
      tracker.setValue(element.value + +new Date());
    }
    element.dispatchEvent(ev);
    element.type = 'hidden';
  };
  const handleChange = (value, actionMeta) => {
    setLocalValue(value);
    setErrorMessage(null);
    onChange && onChange(value, actionMeta);
    triggerChangeForHiddenField(actionMeta.name);
  };
  rest.inputId = `${+new Date()}_select`;
  const customStyles = {
    menuPortal: base => ({
      ...base,
      zIndex: 99999,
      boxShadow: '0 0 6px #ccc',
      maxHeight: '200px',
      width: 'auto',
      // Ensure dropdown appears above all elements
      whiteSpace: 'nowrap',
      // Prevents text from wrapping
      overflow: 'hidden' // Avoids horizontal scroll
    })
  };
  const selectComponentOptions = {
    ...rest,
    dropdownPortal,
    ref: elementReference,
    className: `MuiAutocomplete-root ${throwError && !localValue && 'error'} ${className} ${id}`,
    inputId: 'react-select-single',
    TextFieldProps: {
      label: label,
      variant: 'outlined',
      InputLabelProps: {
        htmlFor: rest.inputId,
        shrink: true
      }
    },
    name,
    placeholder,
    options: options?.map(x => ({
      value: x.value || x.id,
      label: x.label || x.name
    })),
    components,
    value: localValue,
    onChange: handleChange,
    isMulti: isMultiSelect,
    styles: dropdownPortal && customStyles,
    menuPortalTarget: dropdownPortal && document.body
  };
  if (options && isMultiSelect && typeof selectComponentOptions.value === 'string') {
    const initialValues = selectComponentOptions.value.split(',')?.filter(x => x) || [];
    selectComponentOptions.value = options.filter(x => initialValues.includes(x.value));
  }
  if (errorMessage) {
    selectComponentOptions.TextFieldProps.helperText = t(errorMessage.message).replace('[nome]', label).replace('[errormsgparam]', errorMessage.param);
    selectComponentOptions.TextFieldProps.error = true;
  }
  if (error) {
    selectComponentOptions.TextFieldProps.error = true;
    selectComponentOptions.TextFieldProps.helperText = helperText;
  }
  return /*#__PURE__*/React.createElement(FormControl, {
    variant: "outlined",
    margin: margin,
    className: className,
    fullWidth: fullWidth
  }, /*#__PURE__*/React.createElement(Select, selectComponentOptions));
};
export default Autocomplete;
Autocomplete.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  value: PropTypes.any,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  options: PropTypes.array
};